import { AHNumberInput, Space } from "arkh";
import React, { useCallback } from "react";

import { TaskExecutionProps } from "./TaskExecutionProps";
import { useDebouncedMutation } from "../../../../common/Utils/useDebouncedMutation";

import { graphql, useFragment, useMutation } from "react-relay";
import { TaskExecutionNumberComponent_TaskExecutionNumber$key } from "./__generated__/TaskExecutionNumberComponent_TaskExecutionNumber.graphql";
import { TaskExecutionNumberSetValueMutation } from "./__generated__/TaskExecutionNumberSetValueMutation.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

const TaskExecutionNumberFragmentRelay = graphql`
  fragment TaskExecutionNumberComponent_TaskExecutionNumber on TaskExecutionNumber {
    id
    task {
      id
      unit
    }
    numberValue
  }
`;

export const TaskExecutionNumber = (
  props: Omit<
    TaskExecutionProps & {
      spaceVertical?: Space;
      taskExecution: TaskExecutionNumberComponent_TaskExecutionNumber$key;
      tabIndex?: number;
    },
    "openIssue" | "openIssueFull" | "openInstructions" | "openImages"
  > & { hideGoals?: boolean; hideButtons?: boolean }
) => {
  const te = useFragment(TaskExecutionNumberFragmentRelay, props.taskExecution);
  const { currentEmployeeId } = usePersistedEmployeeId();

  const [save] = useMutation<TaskExecutionNumberSetValueMutation>(graphql`
    mutation TaskExecutionNumberSetValueMutation(
      $id: ID!
      $value: Float!
      $employeeId: ID
    ) {
      setTaskExecutionNumber(id: $id, value: $value, employeeId: $employeeId) {
        id
        ...TaskExecutionNumberComponent_TaskExecutionNumber
        numberValue
        formattedNumberValue
        isCompleted
        occasion {
          id
          sum
          isCompleted
        }
      }
    }
  `);

  const onSave = useCallback(
    (val: number | undefined) => {
      if (val === undefined) {
        return;
      }
      save({
        variables: {
          id: te.id,

          value: val,
          employeeId: currentEmployeeId,
        },
      });
    },
    [save, te.id, currentEmployeeId]
  );

  const remoteValue = te.numberValue ?? undefined;

  const [localValue, setValue] = useDebouncedMutation(remoteValue, onSave, 400);

  const maxVal = 99999999999;
  const minVal = -9999999999;

  return (
    <AHNumberInput
      suffix={
        te.task.unit === "CURRENCY" && localValue !== undefined
          ? "kr"
          : undefined
      }
      spaceVertical={props.spaceVertical ?? "large"}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      disabled={props.disabled ?? false}
      setValue={setValue}
      value={localValue}
      maxValue={maxVal}
      minValue={minVal}
      buttonsShow={!props.hideButtons}
      clearTextOnFocus={!te || !te.numberValue}
      tabIndex={props.tabIndex}
    />
  );
};
